'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Building, FlaskConical, Globe2, Landmark, Tag } from 'lucide-react';
import { EscudoUda } from '@/components/investigador/sidebar/EscudoUda';
import {
	claveDeEtiqueta,
	desplazamientoParaCaber,
	esTarjeta,
	lineasDeEtiqueta,
	type HoverTipCard,
	type HoverTipIcon,
	type HoverTipLabel,
} from './hoverTipCard';
import styles from './HoverTip.module.css';

export type { HoverTipCard, HoverTipLabel };

/** Hacia dónde se abre la etiqueta desde el control que la dispara. */
type HoverTipSide = 'left' | 'right';

/** Aire entre el borde del control y la etiqueta. */
const GAP = 8;

/** Aire entre el borde de la etiqueta y el borde de la ventana cuando hay que desplazarla. */
const VIEWPORT_MARGIN = 8;

/** Iconos que puede llevar una tarjeta (el modelo sólo guarda el nombre, ver `hoverTipCard.ts`). */
const ICONOS: Record<HoverTipIcon, ComponentType<{ size?: number; className?: string }>> = {
	flask: FlaskConical,
	building: Building,
	book: BookOpen,
	globe: Globe2,
	tag: Tag,
	landmark: Landmark,
	escudo: EscudoUda,
};

interface HoverTipOptions {
	/**
	 * Deja que la etiqueta se parta en varias líneas (tope de ancho propio) en vez de una sola con
	 * `white-space: nowrap`. Es para textos que pueden ser largos —el nombre completo de una línea de
	 * investigación—; los botones de icono siguen con la etiqueta corta de siempre.
	 */
	wrap?: boolean;
	/**
	 * Hace la etiqueta usable SIN mouse: se ve en todos los anchos (el resto se apaga bajo 1024px, ver el CSS),
	 * un toque en el ancla la muestra y un toque en cualquier otro lado la oculta. Es para la información que
	 * sólo vive en la etiqueta —el nombre completo de una línea, su unidad y sus áreas— y no puede quedar
	 * fuera de alcance en un celular. El foco de teclado ya la muestra siempre; Escape la cierra.
	 */
	touch?: boolean;
	/**
	 * Etiqueta de AYUDA, para leer y no solo para orientar: más ancha, con más aire y letra más grande. Es
	 * para explicaciones de varios renglones (qué muestra una vista); implica `wrap`.
	 */
	amplio?: boolean;
	/** Icono junto al título (la primera línea) de una etiqueta estructurada. */
	icono?: ComponentType<{ size?: number; className?: string }>;
}

/** Lo único que se necesita del evento: quién lo recibió, para medirlo. */
interface AnchorEvent {
	currentTarget: HTMLElement;
}

/** El evento de puntero agrega con qué dedo o aparato se hizo (`touch`, `mouse`, `pen`). */
interface PointerAnchorEvent extends AnchorEvent {
	pointerType: string;
	/** Dónde cayó el toque: un control interno del ancla (un botón de la fila) se queda con su propio toque. */
	target: EventTarget;
}

/** Controles que, dentro del ancla, atienden su propio toque. */
const CONTROLES_INTERNOS = 'button, a, input, select, textarea, [role="switch"]';

/** Si el toque cayó sobre un control DENTRO del ancla (no sobre el ancla misma ni fuera de ella). */
function esToqueDeControlInterno(event: PointerAnchorEvent): boolean {
	if (!(event.target instanceof Element)) return false;
	const control = event.target.closest(CONTROLES_INTERNOS);
	return control !== null && control !== event.currentTarget && event.currentTarget.contains(control);
}

/** Lo que se está mostrando. Guarda su propia etiqueta para poder detectar que
 *  quedó vieja — ver el porqué en el hook. */
interface ShownTip {
	/** Identidad por CONTENIDO (`claveDeEtiqueta`): la etiqueta estructurada llega como objeto o arreglo NUEVO
	 *  en cada render, así que la vigencia no se puede juzgar por referencia. */
	key: string;
	/** Las líneas de una etiqueta de texto; vacío cuando lo que se muestra es una tarjeta. */
	lines: readonly string[];
	/** La tarjeta, cuando la etiqueta lo es. */
	card: HoverTipCard | null;
	top: number;
	left: number;
}

interface HoverTip {
	/** Se esparcen sobre el control. El ancla es ese mismo elemento, así que no
	 *  hace falta pasear un `ref`: el rectángulo sale del `currentTarget`. */
	handlers: {
		onMouseEnter: (event: AnchorEvent) => void;
		onMouseLeave: () => void;
		onFocus: (event: AnchorEvent) => void;
		onBlur: () => void;
		/** Sólo con `touch`: el toque que TERMINA sobre el ancla la muestra (un gesto de scroll acaba en
		 *  `pointercancel`, no en `pointerup`, así que desplazarse sobre una fila no la dispara). */
		onPointerUp?: (event: PointerAnchorEvent) => void;
	};
	/** El portal. Se escribe dentro del control pero termina colgado de `document.body`. */
	tip: ReactNode;
}

/**
 * Etiqueta inmediata para controles que sólo muestran un icono.
 *
 * Existe porque el `title` del navegador no sirve para esto, por dos motivos
 * distintos: llega uno o dos segundos tarde —en una columna de iconos, demasiado
 * para orientar a nadie— y el navegador lo dibuja donde quiere, encima de esta
 * etiqueta. Los dos no pueden convivir, así que **quien use este hook no debe poner
 * `title` en el mismo elemento**. Y no hay forma de apagar el nativo por CSS: la
 * única manera de que no salga es que el atributo no esté. El nombre accesible no se
 * pierde, porque nunca dependió del `title` sino del `aria-label`.
 *
 * `label` puede ser un arreglo de líneas (etiqueta estructurada: la primera es el título, las demás el detalle),
 * pensado para `wrap`, o una TARJETA (`HoverTipCard`: cabecera con icono y filas con rótulo), que es la
 * variante rica de la fila de línea de investigación. Cada render puede pasar un arreglo u objeto nuevo: la
 * vigencia se compara por contenido.
 *
 * Con `touch` la etiqueta también funciona sin mouse (ver `HoverTipOptions.touch`): visible bajo 1024px, un
 * toque en el ancla la abre y un toque fuera la cierra. El resto de los usos sigue como antes.
 *
 * `label` en `null` apaga la etiqueta sin romper el orden de los hooks — el caso del
 * aside izquierdo desplegado, donde el nombre ya se lee al lado del icono.
 *
 * El ancla es el elemento que recibe los `handlers`, no el que tiene el foco: `onFocus` y
 * `onBlur` de React burbujean, así que un control interno enfocado (el "…" de una fila de
 * línea) muestra la etiqueta de la FILA entera, sin sumar paradas de tabulador.
 */
export function useHoverTip(label: HoverTipLabel | null, side: HoverTipSide, options: HoverTipOptions = {}): HoverTip {
	const [shown, setShown] = useState<ShownTip | null>(null);
	const wrap = options.wrap === true || options.amplio === true;
	const touch = options.touch === true;
	const amplio = options.amplio === true;
	const Icono = options.icono;
	const key = claveDeEtiqueta(label);
	const tipRef = useRef<HTMLSpanElement>(null);
	// El elemento que disparó la etiqueta: un toque fuera de él la oculta.
	const anchorRef = useRef<HTMLElement | null>(null);

	// Si la etiqueta cambió, la medición guardada ya no vale y hay que descartarla.
	// El caso real es el botón de la barra lateral: al expandirla se cambia por su
	// gemelo, en otra posición y con otro texto, y React desmonta el anterior SIN
	// disparar `mouseleave` —el mouse nunca se movió—, así que el globo se quedaba
	// flotando donde estaba el botón viejo. Ajustar estado durante el render es el
	// patrón que React recomienda para esto: vuelve a renderizar antes de pintar, así
	// que no se ve el estado intermedio.
	if (shown && shown.key !== key) setShown(null);

	// La etiqueta (arreglo u objeto) se reconstruye en cada render pero sólo cambia su contenido, que es lo que
	// dice `key`: por eso el `show` nuevo de cada render no es un problema, nadie lo compara por identidad.
	const show = (event: AnchorEvent) => {
		if (!key) return;
		anchorRef.current = event.currentTarget;
		const rect = event.currentTarget.getBoundingClientRect();
		setShown({
			key,
			lines: lineasDeEtiqueta(label),
			card: esTarjeta(label) ? label : null,
			top: rect.top + rect.height / 2,
			left: side === 'right' ? rect.right + GAP : rect.left - GAP,
		});
	};

	const hide = useCallback(() => setShown(null), []);

	// Un toque en la pantalla no dispara `mouseleave`: sin esto la etiqueta abierta con un toque se quedaría
	// hasta el próximo. Se escucha en captura para verlo antes de que alguien lo detenga, y el ancla queda
	// afuera del chequeo (tocarla de nuevo no la cierra).
	const touchVisible = touch && shown !== null;
	useEffect(() => {
		if (!touchVisible) return;
		const outside = (event: PointerEvent) => {
			const anchor = anchorRef.current;
			if (anchor && event.target instanceof Node && anchor.contains(event.target)) return;
			hide();
		};
		document.addEventListener('pointerdown', outside, true);
		return () => document.removeEventListener('pointerdown', outside, true);
	}, [touchVisible, hide]);

	// La etiqueta es `fixed` y sus coordenadas se midieron al entrar: si el contenedor del ancla
	// hace scroll con el mouse quieto encima (la barra lateral del perfil), el mouse no dispara
	// `mouseleave` y la etiqueta se quedaría flotando donde estaba la fila.
	const visible = shown !== null;
	useEffect(() => {
		if (!visible) return;
		const onEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') hide();
		};
		window.addEventListener('scroll', hide, true);
		window.addEventListener('keydown', onEscape);
		return () => {
			window.removeEventListener('scroll', hide, true);
			window.removeEventListener('keydown', onEscape);
		};
	}, [visible, hide]);

	// La tarjeta es alta y ancha (hasta ~640px) y se centra en la fila: cerca del borde de la ventana se saldría.
	// Se mide ya pintada en el DOM (antes del primer cuadro) y se corre lo justo para que quede adentro, en
	// vertical y en horizontal. La ayuda `amplio` también pasa por acá; el resto de las etiquetas de texto no:
	// son de una o dos líneas y quedan como siempre.
	useLayoutEffect(() => {
		const el = tipRef.current;
		if (!el || (!shown?.card && !amplio) || !shown) return;
		el.style.top = `${shown.top}px`;
		el.style.left = `${shown.left}px`;
		const { x, y } = desplazamientoParaCaber(
			el.getBoundingClientRect(),
			{ ancho: window.innerWidth, alto: window.innerHeight },
			VIEWPORT_MARGIN,
		);
		if (y !== 0) el.style.top = `${shown.top + y}px`;
		if (x !== 0) el.style.left = `${shown.left + x}px`;
	}, [shown, amplio]);

	const sideClass = `${side === 'right' ? styles.tipRight : styles.tipLeft}${touch ? ` ${styles.tipTouch}` : ''}`;

	return {
		handlers: {
			onMouseEnter: show,
			onMouseLeave: hide,
			onFocus: show,
			onBlur: hide,
			...(touch
				? {
						onPointerUp: (event: PointerAnchorEvent) => {
							if (event.pointerType === 'touch' && !esToqueDeControlInterno(event)) show(event);
						},
					}
				: {}),
		},
		tip:
			shown && typeof document !== 'undefined'
				? createPortal(
						shown.card ? (
							<span
								ref={tipRef}
								role="tooltip"
								className={`${styles.tip} ${styles.tipCard} ${sideClass}`}
								style={{ top: shown.top, left: shown.left }}
							>
								<TooltipCard card={shown.card} />
							</span>
						) : (
							<span
								ref={amplio ? tipRef : undefined}
								role="tooltip"
								className={`${styles.tip} ${sideClass}${wrap ? ` ${styles.tipWrap}` : ''}${amplio ? ` ${styles.tipAmplio}` : ''}`}
								style={{ top: shown.top, left: shown.left }}
							>
								{shown.lines.length === 1
									? shown.lines[0]
									: shown.lines.map((linea, indice) => (
											<span key={indice} className={indice === 0 ? styles.tipTitle : styles.tipDetail}>
												{indice === 0 && Icono ? <Icono size={15} className={styles.tipTitleIcon} aria-hidden="true" /> : null}
												{linea}
											</span>
										))}
							</span>
						),
						document.body,
					)
				: null,
	};
}

/**
 * Contenido de una tarjeta: cabecera (icono + título, y un rótulo atenuado debajo si lo hay) y UNA línea por
 * dato: icono, rótulo en versalitas (columna de ancho fijo, para que los valores queden alineados) y los
 * valores. El detalle de un valor (la subárea de un área OCDE) va en la MISMA línea, tras un separador y
 * atenuado. Todo `span` en bloque: va dentro del `span role="tooltip"` y no necesita más semántica que el texto.
 */
function TooltipCard({ card }: { card: HoverTipCard }) {
	const HeaderIcon = ICONOS[card.icon];
	return (
		<>
			<span className={styles.cardHead}>
				{card.icon === 'escudo' ? (
					// Tooltip version of the UDA shield: same shape, bigger (20px, a finer 1.6 stroke so the real line
					// thickness stays about the same) with less padding, keeping the 25px tile of the other header icons.
					<EscudoUda size={20} trazo={1.6} className={`${styles.cardHeadIcon} ${styles.cardHeadIconEscudo}`} />
				) : (
					<HeaderIcon size={15} className={styles.cardHeadIcon} aria-hidden="true" />
				)}
				<span className={styles.cardHeadText}>
					<span className={`${styles.cardTitle}${card.titleUnaLinea ? ` ${styles.cardUnaLinea}` : ''}`} lang="es">{card.title}</span>
					{card.caption ? <span className={styles.cardCaption}>{card.caption}</span> : null}
				</span>
			</span>
			{card.rows.length > 0 ? (
				<span className={styles.cardRows}>
					{card.rows.map((row) => {
						const RowIcon = ICONOS[row.icon];
						return (
							<span key={row.label} className={styles.cardRow}>
								<RowIcon size={13} className={styles.cardRowIcon} aria-hidden="true" />
								<span className={styles.cardRowLabel}>{row.label}</span>
								<span className={styles.cardRowValues}>
									{row.values.map((value, indice) => (
										<span key={indice} className={styles.cardValue}>
											<span className={`${styles.cardValueText}${value.unaLinea ? ` ${styles.cardUnaLinea}` : ''}`}>{value.text}</span>
											{value.detail ? (
												<>
													{' '}
													<span className={styles.cardSep} aria-hidden="true">
														›
													</span>{' '}
													<span className={styles.cardValueDetail}>{value.detail}</span>
												</>
											) : null}
										</span>
									))}
								</span>
							</span>
						);
					})}
				</span>
			) : null}
		</>
	);
}
